import { useState } from 'react';

/**
 * Spec 352 §4: persisted Kachel↔Tabelle view mode for /services.
 *
 * Per-browser/-user via localStorage (there is no server-side user-pref store, and
 * introducing one would breach the consumer boundary). Modelled byte-for-byte on
 * the `useSnippetLanguage` idiom: lazy init from localStorage, best-effort writes
 * in try/catch (private-mode safe), guard + hard default. Default is `card` so the
 * first visit lands on the trained view; subsequent visits restore the last choice.
 *
 * The view mode is deliberately NOT mirrored into the URL (Decision #8): a shared
 * filter/counter link carries no `view`, so the recipient keeps their own last-used
 * view instead of having the sharer's forced on them.
 */

const VIEW_MODE_STORAGE_KEY = 'services.viewMode';

export type ServiceViewMode = 'card' | 'table';

function isViewMode(value: string | null): value is ServiceViewMode {
  return value === 'card' || value === 'table';
}

export function useServicesViewMode(): [ServiceViewMode, (mode: ServiceViewMode) => void] {
  const [viewMode, setViewMode] = useState<ServiceViewMode>(() => {
    try {
      const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      return isViewMode(stored) ? stored : 'card';
    } catch {
      return 'card';
    }
  });

  const update = (next: ServiceViewMode) => {
    setViewMode(next);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
    } catch {
      // Persistenz ist Komfort — die Auswahl gilt trotzdem fuer die Session.
    }
  };

  return [viewMode, update];
}
